import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { IonCard, IonCardHeader, IonCardTitle, IonCardContent, IonButton, IonIcon, IonSpinner, IonContent, IonList, IonItem, IonLabel, IonChip } from "@ionic/react";
import { informationCircleOutline, copyOutline, shareSocialOutline, caretDownOutline } from "ionicons/icons";
import { ResponsiveContainer, BarChart, Bar, ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import AppLayout from "@/components/AppLayout";
import AppSeo from "@/components/AppSeo";
import FreshPopover from "@/components/FreshPopover";
import { isUserLoggedIn, userHeaders } from "@/utils/userAuth";
import { useAppLive } from "@/utils/appLive";
import { toast } from "@/utils/toast";
import { t } from "@/utils/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

// /app/referrals (signed-in accounts): the referral program card. The terms,
// the account's referral link (unlocked by a live plan, masked until then),
// then an Analytics bar with a range dropdown over two charts: sign-ups
// through the link per day, and the plans those sign-ups bought per day with
// the credit they earned on a dollar axis.
const RANGES = [{ days: 7, label: "Last 7 days" }, { days: 30, label: "Last 30 days" }, { days: 90, label: "Last 90 days" }];
const TIERS = [
  { name: "Enterprise", color: "#f97316" },
  { name: "Pro", color: "#3b82f6" },
  { name: "Express", color: "#ec4899" },
];
const CREDIT_COLOR = "#2dd36f";
const SIGNUP_COLOR = "#94a3b8";
const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const shortDate = (iso) => { const [y, m, d] = String(iso).split("-"); return `${Number(m)}/${Number(d)}/${String(y).slice(2)}`; };
const tooltipStyle = { background: "var(--ion-card-background)", border: "1px solid var(--ion-border-color)", borderRadius: 8, color: "var(--ion-text-color)", fontSize: 12 };

// the legend above each chart: a dot and a name, centred, like the design
const Legend = ({ items }) => (
  <div className="ref-legend">
    {items.map((i) => (
      <span key={i.name} className="ref-legend-item"><span className="ref-legend-dot" style={{ background: i.color }} />{i.name}</span>
    ))}
  </div>
);

export default function AppReferrals() {
  const navigate = useNavigate();
  const signedIn = isUserLoggedIn();
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);   // the /api/user/referrals answer
  const [error, setError] = useState("");
  const [rangeMenu, setRangeMenu] = useState({ open: false, event: undefined });
  const [infoMenu, setInfoMenu] = useState({ open: false, event: undefined }); // the info icon's note

  const load = useCallback(async (silent = false) => {
    if (!isUserLoggedIn()) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/user/referrals?days=${days}`, { headers: userHeaders() });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || d.success === false) throw new Error(d.detail || "Could not load your referrals");
      setData(d);
      setError("");
    } catch (err) { if (!silent) setError(err.message); }
  }, [days]);
  useEffect(() => { load(); }, [load]);
  useAppLive(() => load(true));

  const link = data?.link || "";
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(link); toast.success("Referral link copied"); }
    catch { toast.error("Could not copy the link"); }
  };
  const shareLink = async () => {
    try { await navigator.share({ title: "MintSlip", text: "Join MintSlip through my referral link", url: link }); }
    catch { /* the share sheet was dismissed */ }
  };
  const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";

  // the chart rows: dates as M/D/YY, one bar per plan name, the credit on its own axis
  const signups = (data?.signups || []).map((r) => ({ label: shortDate(r.date), count: r.count }));
  const credits = (data?.credits || []).map((r) => ({ label: shortDate(r.date), amount: r.amount || 0, ...Object.fromEntries(TIERS.map((tier) => [tier.name, (r.tiers || {})[tier.name] || 0])) }));
  const range = signups.length ? `${signups[0].label} - ${signups[signups.length - 1].label}` : "";
  // every day labelled on a wide screen for a week; a phone, or a long range, shows every few
  const narrow = typeof window !== "undefined" && window.innerWidth < 768;
  const tickGap = days > 30 ? (narrow ? 29 : 14) : days > 7 ? (narrow ? 9 : 4) : (narrow ? 2 : 0);
  const tick = { fontSize: narrow ? 10 : 11, fill: "#94a3b8" };

  return (
    <AppLayout>
      <AppSeo title="Referrals" description="Share MintSlip and earn credit toward your next bill." path="/app/referrals" />
      {/* a plain block under the layout's own scroller, like Billing */}
      <div className="app-billing app-referrals" style={{ maxWidth: 1200, margin: "0 auto" }}>
          {!signedIn ? (
            <IonCard>
              <IonCardHeader><IonCardTitle>{t("Referrals")}</IonCardTitle></IonCardHeader>
              <IonCardContent>
                <p>Sign in to get your referral link and see who has joined through it.</p>
                <IonButton onClick={() => navigate("/login")}>{t("Sign in")}</IonButton>
              </IonCardContent>
            </IonCard>
          ) : (
            <IonCard className="ref-card">
              <IonCardHeader><IonCardTitle>MintSlip Referral Program</IonCardTitle></IonCardHeader>
              <IonCardContent className="ref-terms">
                <p>Get MintSlip to credit you!</p>
                <p>Share your referral link with another business and you'll both benefit. When a new account signs up using your referral link and subscribes to a plan, they'll receive {data?.percent || 10}% off their first billing cycle, and you'll automatically receive a {data?.percent || 10}% credit to your Wallet Balance.</p>
                <p>Referral discounts are only available to new accounts subscribing to a plan. Referrers are eligible to receive their referral credit whether they're subscribed to a monthly or annual plan.</p>
              </IonCardContent>

              {/* the link: the account's own once a plan is live, masked until then */}
              <div className="ref-link-block">
                <div className="ref-link-label">
                  Referral Link
                  <IonIcon icon={informationCircleOutline} role="button" tabIndex={0} aria-label="About the referral link"
                    onClick={(e) => setInfoMenu({ open: true, event: e.nativeEvent })} />
                </div>
                {/* the icon's note, in a small popover */}
                <FreshPopover open={infoMenu.open} event={infoMenu.event} onClose={() => setInfoMenu({ open: false, event: undefined })} side="bottom" alignment="start" style={{ "--width": "320px" }}>
                  <p className="ref-info">Use this link to direct other users to signup with MintSlip to get {data?.percent || 10}% off their first billing cycle when they subscribe</p>
                </FreshPopover>
                {error ? (
                  <div className="ref-locked">{error}</div>
                ) : !data ? (
                  <div className="ref-link-masked"><IonSpinner name="crescent" style={{ width: 18, height: 18 }} /></div>
                ) : data.unlocked && link ? (
                  <div className="ref-link-row">
                    <code className="ref-link">{link}</code>
                    <IonButton fill="clear" size="small" className="app-row-icon-btn" onClick={copyLink} aria-label="Copy the link" title="Copy"><IonIcon slot="icon-only" icon={copyOutline} /></IonButton>
                    {canShare && <IonButton fill="clear" size="small" className="app-row-icon-btn" onClick={shareLink} aria-label="Share the link" title="Share"><IonIcon slot="icon-only" icon={shareSocialOutline} /></IonButton>}
                  </div>
                ) : (
                  <>
                    <div className="ref-link-masked" aria-label="Locked">••••••••••••••••••••••••••••</div>
                    {/* the locked notice: a danger chip, pill-shaped and pressable, that leads nowhere */}
                    <IonChip color="danger" className="ref-locked-chip" onClick={() => {}}>You must have an active MintSlip subscription to unlock your referral link</IonChip>
                  </>
                )}
              </div>

              {/* analytics: the range on the right, the two charts under it */}
              <div className="admin-subbar ref-analytics-bar">
                <span>Analytics</span>
                <IonButton fill="clear" size="small" color="medium" className="ref-range" onClick={(e) => setRangeMenu({ open: true, event: e.nativeEvent })}>
                  Days <IonIcon icon={caretDownOutline} /> <span className="ref-range-dates">{range}</span>
                </IonButton>
              </div>
              <FreshPopover open={rangeMenu.open} event={rangeMenu.event} onClose={() => setRangeMenu({ open: false, event: undefined })} side="bottom" alignment="end" style={{ "--width": "240px" }}>
                <IonContent>
                  <IonList style={{ padding: "4px 0" }}>
                    {RANGES.map((r, i) => (
                      <IonItem key={r.days} button detail={false} lines={i === RANGES.length - 1 ? "none" : "inset"}
                        aria-current={days === r.days ? "true" : undefined}
                        onClick={() => { setRangeMenu({ open: false, event: undefined }); setDays(r.days); }}
                        style={{ "--min-height": "48px", "--padding-start": "14px", "--inner-padding-end": "14px", "--background": "transparent", "--color": "var(--ion-text-color)", "--border-color": "var(--app-divider)" }}>
                        <IonLabel>{r.label}</IonLabel>
                      </IonItem>
                    ))}
                  </IonList>
                </IonContent>
              </FreshPopover>

              <div className="ref-charts">
                <div className="ref-chart">
                  <Legend items={[{ name: "Sign-ups", color: SIGNUP_COLOR }]} />
                  <div className="ref-chart-box">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={signups} margin={{ top: 8, right: 8, left: -16, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.22)" vertical={false} />
                        <XAxis dataKey="label" tick={tick} interval={tickGap} />
                        <YAxis allowDecimals={false} tick={tick} width={40} />
                        <Tooltip cursor={{ fill: "rgba(148,163,184,0.1)" }} contentStyle={tooltipStyle} />
                        <Bar dataKey="count" name="Sign-ups" fill={SIGNUP_COLOR} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="ref-chart">
                  <Legend items={[...TIERS, { name: "Credit", color: CREDIT_COLOR }]} />
                  <div className="ref-chart-box">
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={credits} margin={{ top: 8, right: 0, left: -16, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.22)" vertical={false} />
                        <XAxis dataKey="label" tick={tick} interval={tickGap} />
                        <YAxis yAxisId="plans" allowDecimals={false} tick={tick} width={40} />
                        <YAxis yAxisId="credit" orientation="right" tick={{ fontSize: 11, fill: CREDIT_COLOR }} width={56} tickFormatter={(v) => money(v)} />
                        <Tooltip cursor={{ fill: "rgba(148,163,184,0.1)" }} contentStyle={tooltipStyle} formatter={(v, name) => [name === "Credit" ? money(v) : v, name]} />
                        {TIERS.map((tier) => <Bar key={tier.name} yAxisId="plans" dataKey={tier.name} name={tier.name} stackId="plans" fill={tier.color} />)}
                        <Line yAxisId="credit" type="linear" dataKey="amount" name="Credit" stroke={CREDIT_COLOR} strokeWidth={2.5} dot={false} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </IonCard>
          )}
        </div>
    </AppLayout>
  );
}
